import { ReactNode } from "react";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { alpha, styled, Theme } from "@mui/material/styles";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  gridClasses,
  gridFilteredSortedRowIdsSelector,
  useGridApiContext,
} from "@mui/x-data-grid";
import { Button, Tooltip } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { COLORS } from "../../SolarGikLib/Colors";
import classes from "./DataGridTable.module.css";

const ODD_OPACITY = 0.2;
const border = "1px solid var(--table-outline-color)";
const hoverColor = "var(--table-on-hover-color)";

export const StyledDataGridTable = styled(DataGrid)(createTableTheme);

export function createTableTheme({ theme }: { theme: Theme }) {
  return {
    border: "none",
    color: COLORS.DARK_BLUE_PRIMARY,
    fontWeight: "var(--font-weight)",
    fontSize: "14px",

    [`& .${gridClasses.row}.odd`]: {
      "&:hover, &.Mui-hovered": {
        backgroundColor: hoverColor,
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
      "&.Mui-selected": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity
        ),
        "&:hover, &.Mui-hovered": {
          backgroundColor: hoverColor,
          "@media (hover: none)": {
            backgroundColor: hoverColor,
          },
        },
      },
    },
    [`& .${gridClasses.row}.even`]: {
      "&:hover, &.Mui-hovered": {
        backgroundColor: hoverColor,
      },
    },
    [`& .${gridClasses.cell}`]: {
      borderRight: border,
      "&:last-child": {
        borderRight: "none",
      },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "5px 10px",
      "@media screen and (max-width: 1600px)": {
        fontSize: "12px",
      },
    },
    [`& .${gridClasses.cell}.right-border`]: {
      "&:last-child": {
        borderRight: "none",
      },
    },

    [`& .${gridClasses.columnHeader}`]: {
      backgroundColor: "white",
      borderRight: border,
      borderBottom: border,
      "&:last-child": {
        borderRight: "none",
      },
      paddingLeft: "0px",
      fontWeight: "var(--font-bold-weight)",
      fontSize: "14px",
      "@media screen and (max-width: 1600px)": {
        fontSize: "12px",
      },
    },

    [`& .${gridClasses.columnHeader}.right-border-and-bottom`]: {
      "&:last-child": {
        borderRight: "none",
      },
      paddingLeft: "0px",
    },
    [`& .${gridClasses.columnHeader}.main-header`]: {
      "&:last-child": {
        borderRight: "none",
      },
      paddingLeft: "0px",
    },
    "& [class^=HeaderGroupItem_header-group-item]": {
      backgroundColor: "white",
    },
    " & .MuiDataGrid-menuIcon": {
      transitionDelay: "0.3s",
    },
    "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon": {
      transition: "opacity 800ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
    "& .MuiDataGrid-columnHeader .MuiIconButton-root": {
      padding: "11px",
      height: "14px",
      width: "14px",
      "@media screen and (max-width: 1600px)": {
        padding: "9px",
      },
    },

    "& .MuiDataGrid-columnHeaderTitleContainer": {
      boxSizing: "border-box",
      width: "100%",
      padding: "0px",
      margin: "0px",
      border: "0px !important",
      overflow: "visible",
    },
    [`& .MuiDataGrid-columnHeaderTitleContainerContent`]: {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      padding: "0px",
      margin: "0px",
      border: "0px !important",
      overflow: "visible",
    },

    "& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitleContainerContent": {
      justifyContent: "flex-start",
      paddingLeft: "15px",
    },

    "& [class^='HeaderGroupItem_text']": {
      color: COLORS.DARK_BLUE_PRIMARY,
      fontSize: "14px",
      textDecoration: "underline",
    },
    "@media screen and (max-width: 1600px)": {
      "& [class^='HeaderGroupItem_text']": {
        fontSize: "12px",
      },
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      display: "flex",
      flexWrap: "wrap",
      border: "0px solid",
      borderColor: "var(--table-outline-color)",
    },

    "& .MuiSvgIcon-root": {
      color: COLORS.DARK_BLUE_PRIMARY,
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-main": {
      border: border,
      borderRadius: "var(--card-border-radius)",
    },
    "& .MuiDataGrid-overlay": {
      fontSize: "20px",
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
    },
    "& .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
  };
}

const toolbarButtonSx = { minWidth: 0, padding: "4px 20px" };

const CustomGridToolbarContainer = styled(GridToolbarContainer)(({ theme }) => ({
  backgroundColor: "transparent",
  marginBottom: theme.spacing(2),
  alignItems: "center",
  gap: "0",
  flexWrap: "wrap",
  rowGap: theme.spacing(1),
}));

const ToolbarContentWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  flex: "0 1 auto",
  flexWrap: "wrap",
});

const ToolbarActionsWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  maxWidth: "100%",
  marginLeft: "auto",
  flex: "0 0 auto",
  flexWrap: "wrap",
  justifyContent: "flex-end",
});

interface GridToolbarCsvExportButtonProps {
  getFileName?: () => string;
  getCsvRows?: (fields: string[]) => string[][];
}

const GridToolbarCsvExportButton = ({ getFileName, getCsvRows }: GridToolbarCsvExportButtonProps) => {
  const apiRef = useGridApiContext();
  return (
    <Tooltip title="Export">
      <Button
        aria-label="Export table to CSV"
        className={classes.blue}
        size="small"
        sx={toolbarButtonSx}
        onClick={() => {
          if (getCsvRows) {
            const fields = apiRef.current
              .getVisibleColumns()
              .filter((column) => !column.disableExport && column.field !== "__check__")
              .map((column) => column.field);
            saveAs(
              // The UTF-8 byte-order mark lets Excel recognize non-ASCII site names.
              new Blob(["\uFEFF", Papa.unparse(getCsvRows(fields), { escapeFormulae: true })], {
                type: "text/csv;charset=utf-8;",
              }),
              `${getFileName?.() ?? "table"}.csv`
            );
            return;
          }
          apiRef.current.exportDataAsCsv({
            fileName: getFileName?.(),
            utf8WithBom: true,
            escapeFormulas: true,
            getRowsToExport: ({ apiRef }) => gridFilteredSortedRowIdsSelector(apiRef),
          });
        }}
      >
        <FileDownloadOutlinedIcon fontSize="small" />
      </Button>
    </Tooltip>
  );
};

export const GridCsvExportToolbar = ({ getFileName }: GridToolbarCsvExportButtonProps) => (
  <CustomGridToolbarContainer>
    <GridToolbarCsvExportButton getFileName={getFileName} />
  </CustomGridToolbarContainer>
);

interface GridToolbarStyledProps extends GridToolbarCsvExportButtonProps {
  toolbarContent?: ReactNode;
  toolbarActions?: ReactNode;
}

export const GridToolbarStyled = ({
  toolbarContent,
  toolbarActions,
  getFileName,
  getCsvRows,
}: GridToolbarStyledProps) => {
  return (
    <CustomGridToolbarContainer>
      {toolbarContent ? <ToolbarContentWrapper>{toolbarContent}</ToolbarContentWrapper> : null}
      <ToolbarActionsWrapper>
        <GridToolbarColumnsButton
          className={classes.blue}
          sx={{ ...toolbarButtonSx, "& .MuiButton-startIcon": { margin: 0 } }}
        />
        <GridToolbarFilterButton
          className={classes.blue}
          sx={{ ...toolbarButtonSx, "& .MuiButton-startIcon": { margin: 0 } }}
        />
        {toolbarActions}
        <GridToolbarCsvExportButton getFileName={getFileName} getCsvRows={getCsvRows} />
      </ToolbarActionsWrapper>
    </CustomGridToolbarContainer>
  );
};
